import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Stack,
  IconButton,
  MenuItem,
  Typography,
  Box,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useTranslation } from 'react-i18next';
import Swal from 'sweetalert2';
import type { Account, NostroOperationResult, PaymentIntermediariesRecord } from '@/types/accounts';
import { NostroOperationType } from '@/types/accounts';
import type { M3ApiResponse } from '@/types/api';
import {
  useNostroAccountAdjustMutation,
  useNostroAccountAddFundsMutation,
  useNostroAccountWithdrawFundsMutation,
} from './api';

const schema = z.object({
  amount: z.coerce
    .number({ invalid_type_error: 'Must be a number' })
    .finite(),
  idPaymentIntermediary: z.string().optional(),
});

type FormValues = z.infer<typeof schema>;

export interface TopupWithdrawDialogProps {
  operation: NostroOperationType;
  account: Account;
  paymentIntermediaries: PaymentIntermediariesRecord[];
  onClose: (result: M3ApiResponse<NostroOperationResult> | null) => void;
}

function formatXAF(value: number): string {
  return `${new Intl.NumberFormat('fr-FR', { minimumFractionDigits: 0 }).format(value)} XAF`;
}

function creditColor(value: number): string {
  if (value > 0) return 'success.main';
  if (value < 0) return 'error.main';
  return 'warning.main';
}

export function TopupWithdrawDialog({
  operation,
  account,
  paymentIntermediaries,
  onClose,
}: TopupWithdrawDialogProps) {
  const { t } = useTranslation();
  const adjustMutation = useNostroAccountAdjustMutation();
  const addFundsMutation = useNostroAccountAddFundsMutation();
  const withdrawMutation = useNostroAccountWithdrawFundsMutation();

  const isAdjust = operation === NostroOperationType.ADJUST;
  const isPending =
    adjustMutation.isPending || addFundsMutation.isPending || withdrawMutation.isPending;

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { amount: 0, idPaymentIntermediary: '' },
  });

  const amountDigitRegex = /^\d+(\.\d{0,9})?$/;

  const onSubmit = form.handleSubmit(async (values) => {
    if (!isAdjust) {
      if (!values.amount || values.amount <= 0 || !amountDigitRegex.test(String(values.amount))) {
        await Swal.fire(
          t('messages.error', { defaultValue: 'Error' }),
          t('messages.error-fields-missing-invalid', {
            field: t('pages.accounts.dialogTopupWithdraw.amount', { defaultValue: 'Amount' }),
            defaultValue: 'Amount is invalid',
          }),
          'error',
        );
        return;
      }
      if (!values.idPaymentIntermediary) {
        await Swal.fire(
          t('messages.error', { defaultValue: 'Error' }),
          t('messages.error-fields-missing-invalid', {
            field: t('pages.accounts.dialogTopupWithdraw.paymentIntermediary', {
              defaultValue: 'Payment intermediary',
            }),
            defaultValue: 'Payment intermediary is required',
          }),
          'error',
        );
        return;
      }
    }

    const request = {
      accountId: account.id,
      amount: values.amount,
      idPaymentIntermediary: values.idPaymentIntermediary ?? '',
    };

    try {
      let res: M3ApiResponse<NostroOperationResult>;

      if (operation === NostroOperationType.ADJUST) {
        res = await adjustMutation.mutateAsync(request);
      } else if (operation === NostroOperationType.ADD_FUNDS) {
        res = await addFundsMutation.mutateAsync(request);
      } else {
        res = await withdrawMutation.mutateAsync(request);
      }

      if (res.code === 0 || res.code === 103) {
        if (operation === NostroOperationType.WITHDRAW_FUNDS && res.code === 103) {
          if (res.result.transfer) {
            res.result.uniqueReference = res.result.transfer.uniqueReference;
          }
        }
        onClose(res);
      } else {
        await Swal.fire(
          t('messages.error', { defaultValue: 'Error' }),
          res.message,
          'error',
        );
      }
    } catch {
      await Swal.fire(
        t('messages.error', { defaultValue: 'Error' }),
        t('messages.unknown-error', { defaultValue: 'An unknown error occurred' }),
        'error',
      );
    }
  });

  const selectedIntermediary = paymentIntermediaries.find(
    (p) => String(p.id) === form.watch('idPaymentIntermediary'),
  );

  const titleKey =
    operation === NostroOperationType.ADD_FUNDS
      ? 'pages.accounts.dialogTopupWithdraw.topup'
      : operation === NostroOperationType.WITHDRAW_FUNDS
        ? 'pages.accounts.dialogTopupWithdraw.withdraw'
        : 'pages.accounts.dialogTopupWithdraw.adjust';

  const submitDisabled =
    isPending ||
    (isAdjust
      ? form.watch('amount') === null || form.watch('amount') === undefined
      : !form.watch('amount') || form.watch('amount') <= 0);

  return (
    <Dialog open onClose={() => onClose(null)} maxWidth="sm" fullWidth>
      <DialogTitle
        sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
      >
        {account.accountNumber}
        <IconButton onClick={() => onClose(null)} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        <Stack spacing={2}>
          {/* Current balance */}
          <Box>
            <Typography variant="body2" color="text.secondary">
              {t('pages.accounts.dialogDetails.table.header.credit', {
                defaultValue: 'Credit',
              })}
            </Typography>
            <Typography variant="body1" fontWeight={600} color={creditColor(account.credit)}>
              {formatXAF(account.credit)}
            </Typography>
          </Box>

          {/* Payment intermediary select (not for adjust) */}
          {!isAdjust && (
            <Controller
              name="idPaymentIntermediary"
              control={form.control}
              render={({ field, fieldState }) => (
                <TextField
                  {...field}
                  select
                  label={t('pages.accounts.dialogTopupWithdraw.paymentIntermediary', {
                    defaultValue: 'Payment intermediary',
                  })}
                  error={!!fieldState.error}
                  fullWidth
                  size="small"
                >
                  {paymentIntermediaries.map((p) => (
                    <MenuItem key={p.id} value={String(p.id)}>
                      {p.name}
                    </MenuItem>
                  ))}
                </TextField>
              )}
            />
          )}

          {/* Account number read-only (not for adjust) */}
          {!isAdjust && (
            <TextField
              label={t('pages.accounts.dialogTopupWithdraw.accountNumber', {
                defaultValue: 'Account number',
              })}
              value={selectedIntermediary?.accountNumber ?? ''}
              InputProps={{ readOnly: true }}
              fullWidth
              size="small"
            />
          )}

          {/* Amount */}
          <Controller
            name="amount"
            control={form.control}
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                type="number"
                label={t('pages.accounts.dialogTopupWithdraw.amount', {
                  defaultValue: 'Amount',
                })}
                error={!!fieldState.error}
                helperText={fieldState.error?.message}
                inputProps={{ min: 0, step: 0.01 }}
                fullWidth
                size="small"
              />
            )}
          />
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button onClick={() => onClose(null)}>
          {t('pages.accounts.dialogTopupWithdraw.cancel', { defaultValue: 'Cancel' })}
        </Button>
        {operation === NostroOperationType.ADD_FUNDS && (
          <Button variant="contained" color="success" onClick={onSubmit} disabled={submitDisabled}>
            {t(titleKey, { defaultValue: 'Top up' })}
          </Button>
        )}
        {operation === NostroOperationType.WITHDRAW_FUNDS && (
          <Button variant="contained" color="warning" onClick={onSubmit} disabled={submitDisabled}>
            {t(titleKey, { defaultValue: 'Withdraw' })}
          </Button>
        )}
        {operation === NostroOperationType.ADJUST && (
          <Button variant="contained" color="error" onClick={onSubmit} disabled={submitDisabled}>
            {t(titleKey, { defaultValue: 'Adjust' })}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
